import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createCampaign,
  getJobs,
  getJobDetails,
  getJobAudits,
  cancelJob,
  retryJob,
  getJobStats,
  getCustomersBySegments,
  getSegmentStats,
  validateCustomerIds,
  getChannelStatus,
} from "../../services/campaignService";

/* ---------------------------------------------------- */
/* create campaign (unchanged)                          */
/* ---------------------------------------------------- */
export const createCampaignThunk = createAsyncThunk(
  "campaigns/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createCampaign(payload);
      if (res.data?.ok === false) {
        const err =
          res.data.errors?.map((e) => e.message).join(", ") ||
          res.data.error ||
          "Failed to create campaign";
        return rejectWithValue(err);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to create campaign"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* list jobs (backend response shape changed)           */
/* ---------------------------------------------------- */
export const fetchJobsThunk = createAsyncThunk(
  "campaigns/fetchJobs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await getJobs(params);
      if (res.ok === false) {
        return rejectWithValue(res.error || "Failed to fetch jobs");
      }
      // Support both { jobs, pagination } and { data, pagination }
      return {
        jobs: res.jobs || res.data || [],
        pagination: res.pagination,
        reset: params.skip === 0 || !params.skip,
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch jobs"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* job details (unchanged)                              */
/* ---------------------------------------------------- */
export const fetchJobDetailsThunk = createAsyncThunk(
  "campaigns/fetchJobDetails",
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await getJobDetails(jobId);
      if (res.ok === false) {
        return rejectWithValue(res.error || "Failed to fetch job details");
      }
      return res.job;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch job details"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* audits (now expects { data: [...] } from backend)    */
/* ---------------------------------------------------- */
export const fetchJobAuditsThunk = createAsyncThunk(
  "campaigns/fetchJobAudits",
  async ({ jobId, params = {} }, { rejectWithValue }) => {
    try {
      const res = await getJobAudits(jobId, params);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Failed to fetch audits");
      }
      return res.data.data || []; // <-- updated path
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch audits"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* cancel                                               */
/* ---------------------------------------------------- */
export const cancelJobThunk = createAsyncThunk(
  "campaigns/cancelJob",
  async (jobId, { rejectWithValue, dispatch }) => {
    try {
      const res = await cancelJob(jobId);
      if (res.ok === false) {
        return rejectWithValue(res.error || "Failed to cancel job");
      }

      // refresh details
      dispatch(fetchJobDetailsThunk(jobId));
      return res.job || { id: jobId, status: "CANCELLED" };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to cancel job"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* retry (unchanged)                                    */
/* ---------------------------------------------------- */
export const retryJobThunk = createAsyncThunk(
  "campaigns/retryJob",
  async (jobId, { rejectWithValue, dispatch }) => {
    try {
      const res = await retryJob(jobId);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Failed to retry job");
      }
      // refresh list
      dispatch(fetchJobsThunk());
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to retry job"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* stats (unchanged)                                    */
/* ---------------------------------------------------- */
export const fetchJobStatsThunk = createAsyncThunk(
  "campaigns/fetchStats",
  async (period = "day", { rejectWithValue }) => {
    try {
      const res = await getJobStats(period);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Failed to fetch stats");
      }
      return res.data.stats;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch statistics"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* Pull IDs for a given segment bundle                  */
/* ---------------------------------------------------- */
export const fetchCustomersBySegmentsThunk = createAsyncThunk(
  "customers/fetchBySegments",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getCustomersBySegments(payload);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Failed to load customers");
      }
      return res.data; // { customerIds, count, isTestMode, … }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load customers"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* Segment head-count snapshots                         */
/* ---------------------------------------------------- */
export const fetchSegmentStatsThunk = createAsyncThunk(
  "customers/fetchSegmentStats",
  async (segments, { rejectWithValue }) => {
    try {
      const res = await getSegmentStats(segments);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Failed to load stats");
      }
      return res.data; // { stats, timestamp }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load stats"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* Validate an arbitrary list of IDs                    */
/* ---------------------------------------------------- */
export const validateCustomerIdsThunk = createAsyncThunk(
  "customers/validateIds",
  async (ids, { rejectWithValue }) => {
    try {
      const res = await validateCustomerIds(ids);
      if (res.data?.ok === false) {
        return rejectWithValue(res.data.error || "Validation failed");
      }
      return res.data; // { valid, invalid, nonExistent, summary }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Validation failed"
      );
    }
  }
);

/* ---------------------------------------------------- */
/* Get channel status (enabled/disabled)                */
/* ---------------------------------------------------- */
export const fetchChannelStatusThunk = createAsyncThunk(
  "campaigns/fetchChannelStatus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getChannelStatus();
      if (res.data?.ok === false) {
        return rejectWithValue(
          res.data.error || "Failed to fetch channel status"
        );
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch channel status"
      );
    }
  }
);
